import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchFunnelConfig } from '@/services/funnelSettingsApi';

const DEFAULT_STATE = {
  config: null,
  loading: true,
  error: null,
  subdomain: null,
};

const FunnelConfigContext = createContext(DEFAULT_STATE);

const normalizeSubdomain = (value) => {
  if (!value) return '';
  return value.replace(/[^a-z0-9-]/gi, '').toLowerCase();
};

const resolveSubdomain = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const override = normalizeSubdomain(searchParams.get('subdomain') || searchParams.get('sd'));

  if (override) {
    return override;
  }

  const host = window.location.hostname;

  if (!host) {
    return null;
  }

  if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
    const fallback = normalizeSubdomain(import.meta.env.VITE_FUNNEL_SUBDOMAIN);
    return fallback || null;
  }

  const parts = host.split('.').filter(Boolean);

  if (parts.length <= 1) {
    const fallback = normalizeSubdomain(import.meta.env.VITE_FUNNEL_SUBDOMAIN);
    return fallback || null;
  }

  if (parts[0] === 'www' && parts.length > 2) {
    return normalizeSubdomain(parts[1]);
  }

  return normalizeSubdomain(parts[0]);
};

export const FunnelConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const subdomain = resolveSubdomain();
  const fetchedSubdomainsRef = useRef(new Set());
  const inFlightRef = useRef(null);

  // Helper function to set CSS color variables
  const setPrimaryColor = useCallback((primaryColor) => {
    const root = document.documentElement;
    
    if (primaryColor) {
      // Ensure color has # prefix for CSS
      let colorValue = primaryColor.trim();
      if (!colorValue.startsWith('#')) {
        colorValue = '#' + colorValue;
      }
      root.style.setProperty('--primary-color', colorValue);
      
      // Calculate and set related color variants
      // Parse hex color (supports both 3-digit and 6-digit hex codes)
      let hex = colorValue.replace('#', '');
      
      // Convert 3-digit hex to 6-digit
      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }
      
      // Validate hex color
      if (hex.length === 6 && /^[0-9A-Fa-f]{6}$/.test(hex)) {
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Calculate hover color (darker)
        const hoverR = Math.max(0, Math.floor(r * 0.9));
        const hoverG = Math.max(0, Math.floor(g * 0.9));
        const hoverB = Math.max(0, Math.floor(b * 0.9));
        root.style.setProperty('--primary-hover', `rgb(${hoverR}, ${hoverG}, ${hoverB})`);
        
        // Calculate light color (lighter)
        const lightR = Math.min(255, Math.floor(r + (255 - r) * 0.2));
        const lightG = Math.min(255, Math.floor(g + (255 - g) * 0.2));
        const lightB = Math.min(255, Math.floor(b + (255 - b) * 0.2));
        root.style.setProperty('--primary-light', `rgb(${lightR}, ${lightG}, ${lightB})`);
        
        // Calculate gradient color
        const gradientR = Math.min(255, Math.floor(r + (255 - r) * 0.15));
        const gradientG = Math.min(255, Math.floor(g + (255 - g) * 0.15));
        const gradientB = Math.min(255, Math.floor(b + (255 - b) * 0.15));
        root.style.setProperty('--primary-gradient', `rgb(${gradientR}, ${gradientG}, ${gradientB})`);
        
        // Set alpha variants
        root.style.setProperty('--primary-alpha-00', `rgba(${r}, ${g}, ${b}, 0)`);
        root.style.setProperty('--primary-alpha-05', `rgba(${r}, ${g}, ${b}, 0.05)`);
        root.style.setProperty('--primary-alpha-08', `rgba(${r}, ${g}, ${b}, 0.08)`);
        root.style.setProperty('--primary-alpha-10', `rgba(${r}, ${g}, ${b}, 0.1)`);
        root.style.setProperty('--primary-alpha-15', `rgba(${r}, ${g}, ${b}, 0.15)`);
        root.style.setProperty('--primary-alpha-20', `rgba(${r}, ${g}, ${b}, 0.2)`);
        root.style.setProperty('--primary-alpha-25', `rgba(${r}, ${g}, ${b}, 0.25)`);
        root.style.setProperty('--primary-alpha-30', `rgba(${r}, ${g}, ${b}, 0.3)`);
        root.style.setProperty('--primary-alpha-40', `rgba(${r}, ${g}, ${b}, 0.4)`);
        root.style.setProperty('--primary-alpha-60', `rgba(${r}, ${g}, ${b}, 0.6)`);
      }
    } else {
      // Fallback to default color from CSS if no primary_color is provided
      root.style.setProperty('--primary-color', '#FF6B35');
    }
  }, []);

  const loadConfig = useCallback(
    async (currentSubdomain, { force = false } = {}) => {
      if (!currentSubdomain) {
        setError('Unable to resolve funnel sub-domain.');
        setLoading(false);
        return;
      }

      if (!force) {
        if (inFlightRef.current === currentSubdomain) {
          return;
        }

        if (fetchedSubdomainsRef.current.has(currentSubdomain)) {
          return;
        }
      }

      inFlightRef.current = currentSubdomain;
      setLoading(true);
      setError(null);

      try {
        const response = await fetchFunnelConfig(currentSubdomain);
        if (!response.success) {
          setError(response.error || 'Unable to load funnel configuration.');
          setConfig(null);
          setPrimaryColor(null); // Reset to default
        } else {
          const configData = response.data?.data || response.data;
          
          // Set CSS color variable IMMEDIATELY before React re-renders
          // This prevents the color flash
          if (configData?.primary_color) {
            setPrimaryColor(configData.primary_color);
          } else {
            setPrimaryColor(null); // Reset to default
          }
          
          setConfig(configData);
          fetchedSubdomainsRef.current.add(currentSubdomain);
        }
      } finally {
        inFlightRef.current = null;
        setLoading(false);
      }
    },
    [setPrimaryColor]
  );

  // Set color immediately on mount if we have cached config (prevents flash)
  useEffect(() => {
    // Check if we have a cached config in sessionStorage
    if (typeof window !== 'undefined' && subdomain) {
      try {
        const cached = sessionStorage.getItem(`funnel_config_${subdomain}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.primary_color) {
            setPrimaryColor(parsed.primary_color);
          }
        }
      } catch (e) {
        // Ignore cache errors
      }
    }
  }, [subdomain, setPrimaryColor]);

  useEffect(() => {
    loadConfig(subdomain);
  }, [subdomain, loadConfig]);

  // Also update CSS variable when config changes (as a backup, but primary update happens in loadConfig)
  useEffect(() => {
    if (config?.primary_color) {
      setPrimaryColor(config.primary_color);
      // Cache the config for faster subsequent loads
      if (typeof window !== 'undefined' && subdomain) {
        try {
          sessionStorage.setItem(`funnel_config_${subdomain}`, JSON.stringify(config));
        } catch (e) {
          // Ignore storage errors
        }
      }
    } else if (config !== null) {
      // Only set default if config has been loaded (not null)
      setPrimaryColor(null);
    }
  }, [config?.primary_color, config, setPrimaryColor, subdomain]);

  const value = useMemo(
    () => ({
      config,
      loading,
      error,
      subdomain,
      refetch: () => subdomain && loadConfig(subdomain, { force: true }),
    }),
    [config, loading, error, subdomain, loadConfig]
  );

  return <FunnelConfigContext.Provider value={value}>{children}</FunnelConfigContext.Provider>;
};

export const useFunnelConfig = () => useContext(FunnelConfigContext);


